"use client";

import { Alert, MenuItem, Skeleton, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Formik } from "formik";
import React, { useState } from "react";
import { mixed, number, object, string } from "yup";

import { MDButton } from "@/components/atoms/MDButton";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { TALK2PAY_PERMISSIONS } from "@/constants/modules.permissions";
import useAuth from "@/contexts/auth-context";
import useToast from "@/contexts/toast-context";
import { buildErrorMessage } from "@/helpers/error-helpers";
import {
  useGetCommercialInfo,
  useUpdateCommercialInfo,
} from "@/hooks/requests/t2Pay/use-commercial-info";
import {
  CommercialCompanyType,
  CommercialInfo,
  CommercialInfoInput,
  TaxRegime,
} from "@/interfaces/talk2pay.iterfaces";

const COMPANY_TYPES: { value: CommercialCompanyType; label: string }[] = [
  { value: "MEI", label: "MEI" },
  { value: "LIMITED", label: "Limitada" },
  { value: "INDIVIDUAL", label: "Individual" },
  { value: "ASSOCIATION", label: "Associação" },
];

const TAX_REGIMES: { value: TaxRegime; label: string }[] = [
  { value: "MEI", label: "MEI" },
  { value: "NATIONAL_SIMPLE", label: "Simples Nacional" },
  { value: "NORMAL_REGIME", label: "Regime Normal" },
  { value: "UNKNOWN", label: "Não informado" },
];


type FormValues = Omit<CommercialInfoInput, "incomeValue"> & { incomeValue: number | string };

const onlyDigits = (v?: string | null) => (v ?? "").replace(/\D/g, "");

const toForm = (d: CommercialInfo | null): FormValues => ({
  personType: d?.personType ?? "JURIDICA",
  cpfCnpj: d?.cpfCnpj ?? "",
  birthDate: d?.birthDate ? String(d.birthDate).slice(0, 10) : "",
  companyType: d?.companyType ?? null,
  companyName: d?.companyName ?? d?.name ?? "",
  incomeValue: d?.incomeValue ?? "",
  taxRegime: d?.taxRegime ?? null,
  email: d?.email ?? "",
  phone: d?.phone ?? "",
  mobilePhone: d?.mobilePhone ?? "",
  site: d?.site ?? "",
  postalCode: d?.postalCode ?? "",
  address: d?.address ?? "",
  addressNumber: d?.addressNumber ?? "",
  complement: d?.complement ?? "",
  province: d?.province ?? "",
});

const nullIfEmpty = (v?: string | null) => (v && v.trim() ? v.trim() : null);

const toPayload = (v: FormValues): CommercialInfoInput => {
  const isPj = v.personType === "JURIDICA";
  return {
    personType: v.personType,
    cpfCnpj: onlyDigits(v.cpfCnpj),
    birthDate: isPj ? null : nullIfEmpty(v.birthDate),
    companyType: isPj ? v.companyType : null,
    companyName: isPj ? nullIfEmpty(v.companyName) : null,
    incomeValue: Number(v.incomeValue),
    taxRegime: v.taxRegime || null,
    email: v.email.trim(),
    phone: nullIfEmpty(onlyDigits(v.phone)),
    mobilePhone: nullIfEmpty(onlyDigits(v.mobilePhone)),
    site: nullIfEmpty(v.site),
    postalCode: onlyDigits(v.postalCode),
    address: v.address.trim(),
    addressNumber: v.addressNumber.trim(),
    complement: nullIfEmpty(v.complement),
    province: v.province.trim(),
  };
};

const sameText = (left?: string | null, right?: string | null) => (left ?? "") === (right ?? "");

const hasReviewRisk = (current: CommercialInfo | null, next: CommercialInfoInput) => {
  const baseline = toPayload(toForm(current));
  return (
    baseline.personType !== next.personType ||
    baseline.cpfCnpj !== next.cpfCnpj ||
    !sameText(baseline.birthDate, next.birthDate) ||
    !sameText(baseline.companyType, next.companyType) ||
    !sameText(baseline.companyName, next.companyName) ||
    Number(baseline.incomeValue) !== Number(next.incomeValue) ||
    !sameText(baseline.taxRegime, next.taxRegime) ||
    baseline.postalCode !== next.postalCode ||
    baseline.address !== next.address ||
    baseline.addressNumber !== next.addressNumber ||
    !sameText(baseline.complement, next.complement) ||
    baseline.province !== next.province
  );
};

const REQUIRED = "Campo obrigatório";

const schema = object({
  email: string().trim().email("E-mail inválido").required(REQUIRED),
  postalCode: string()
    .required(REQUIRED)
    .test("cep", "CEP inválido", (v) => onlyDigits(v).length === 8),
  address: string().trim().required(REQUIRED),
  addressNumber: string().trim().required(REQUIRED),
  province: string().trim().required(REQUIRED),
  incomeValue: number()
    .typeError("Informe um valor")
    .moreThan(0, "Deve ser maior que zero")
    .required(REQUIRED),
  birthDate: string()
    .nullable()
    .when("personType", {
      is: "FISICA",
      then: (s) => s.required(REQUIRED),
    }),
  companyType: mixed()
    .nullable()
    .when("personType", {
      is: "JURIDICA",
      then: (s) => s.required(REQUIRED),
    }),
  companyName: string()
    .nullable()
    .when("personType", {
      is: "JURIDICA",
      then: (s) => s.trim().required(REQUIRED),
    }),
});

function CommercialInfoForm() {
  const { data, isLoading, mutate } = useGetCommercialInfo();
  const { update, isLoading: saving } = useUpdateCommercialInfo();
  const { showToast } = useToast();
  const { checkPermissions } = useAuth();
  const readOnly = checkPermissions(TALK2PAY_PERMISSIONS.CREATE); // inverted: true = no permission
  const [pending, setPending] = useState<CommercialInfoInput | null>(null);

  if (isLoading && !data) return <Skeleton variant="rectangular" width="100%" height={300} />;

  const companyNames = data?.availableCompanyNames ?? [];
  const cityLabel = [data?.city?.name, data?.city?.state].filter(Boolean).join(" / ");

  const confirmSave = async () => {
    if (!pending) return;
    try {
      const result = await update(pending);
      await mutate(result, { revalidate: false });
      showToast({ text: "Dados da empresa atualizados no Asaas", type: "success" });
    } catch (error) {
      showToast({ text: buildErrorMessage(error, "Não foi possível salvar"), type: "error" });
    } finally {
      setPending(null);
    }
  };

  return (
    <Formik
      initialValues={toForm(data)}
      validationSchema={schema}
      enableReinitialize
      onSubmit={(values: FormValues) => setPending(toPayload(values))}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, dirty, resetForm }) => {
        const isPj = values.personType === "JURIDICA";
        const field = (name: keyof FormValues, label: string, extra: object = {}) => ({
          name,
          label,
          value: (values[name] ?? "") as string | number,
          onChange: handleChange,
          onBlur: handleBlur,
          error: !!(touched[name] && errors[name]),
          helperText: touched[name] && (errors[name] as string),
          fullWidth: true,
          variant: "standard" as const,
          disabled: readOnly,
          InputLabelProps: { shrink: true },
          sx: {
            "&& .MuiInputBase-input, && .MuiSelect-select.MuiSelect-select.MuiInputBase-input": {
              padding: "4px 0 5px !important",
              minHeight: "1.4375em",
              lineHeight: "1.4375em",
            },
          },
          ...extra,
        });
        const ro = (label: string, value?: string | null) => (
          <TextField
            label={label}
            value={value || "-"}
            fullWidth
            variant="standard"
            disabled
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiInputBase-input": { padding: "4px 0 5px !important" } }}
          />
        );

        return (
          <form onSubmit={handleSubmit} noValidate>
            {data?.denialReason && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {data.denialReason}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid size={{ md: 4, xs: 12 }}>
                {ro("Tipo de pessoa", isPj ? "Pessoa jurídica" : "Pessoa física")}
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                {ro(isPj ? "CNPJ" : "CPF", applyDocumentMaskSafe(values.cpfCnpj))}
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>{ro("Responsável", data?.responsibleName)}</Grid>

              {isPj ? (
                <>
                  <Grid size={{ md: 4, xs: 12 }}>
                    {companyNames.length > 0 ? (
                      <TextField select {...field("companyName", "Razão social *")}>
                        {companyNames.map((n) => (
                          <MenuItem key={n} value={n}>
                            {n}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <TextField {...field("companyName", "Razão social *")} disabled />
                    )}
                  </Grid>
                  <Grid size={{ md: 4, xs: 12 }}>
                    <TextField select {...field("companyType", "Tipo de empresa *")}>
                      {COMPANY_TYPES.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ md: 4, xs: 12 }}>
                    {ro("Inscrição estadual", data?.inscricaoEstadual)}
                  </Grid>
                </>
              ) : (
                <>
                  <Grid size={{ md: 4, xs: 12 }}>{ro("Nome", data?.name)}</Grid>
                  <Grid size={{ md: 4, xs: 12 }}>
                    <TextField {...field("birthDate", "Data de nascimento *", { type: "date" })} />
                  </Grid>
                </>
              )}

              <Grid size={{ md: 4, xs: 12 }}>
                <TextField select {...field("taxRegime", "Regime tributário")}>
                  {TAX_REGIMES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <TextField
                  {...field("incomeValue", "Faturamento/renda mensal (R$) *", {
                    type: "number",
                    inputProps: { min: 0, step: "0.01" },
                  })}
                />
              </Grid>

              <Grid size={{ md: 4, xs: 12 }}>
                <TextField {...field("email", "E-mail *", { type: "email" })} />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <TextField {...field("mobilePhone", "Celular")} />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <TextField {...field("phone", "Telefone")} />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <TextField {...field("site", "Site")} />
              </Grid>

              <Grid size={12}>
                <MDTypography variant="h6">Endereço</MDTypography>
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <TextField {...field("postalCode", "CEP *")} />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <TextField {...field("address", "Logradouro *")} />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <TextField {...field("addressNumber", "Número *")} />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <TextField {...field("complement", "Complemento")} />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <TextField {...field("province", "Bairro *")} />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>{ro("Cidade / UF", cityLabel)}</Grid>
            </Grid>

            {!readOnly && (
              <MDBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
                <MDButton
                  variant="gradient"
                  color="light"
                  sx={{ minWidth: 120 }}
                  disabled={!dirty || saving}
                  onClick={() => resetForm()}
                >
                  Cancelar
                </MDButton>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="dark"
                  sx={{ minWidth: 120 }}
                  disabled={!dirty || saving}
                >
                  Salvar
                </MDButton>
              </MDBox>
            )}

            <ConfirmationDialog
              isOpen={!!pending}
              onClose={() => setPending(null)}
              onConfirm={confirmSave}
              isLoading={saving}
              titleText="Atualizar dados da empresa?"
              descriptionText={
                pending && hasReviewRisk(data, pending)
                  ? "Os dados serão substituídos no Asaas e a conta pode voltar para análise."
                  : "Os dados serão substituídos no Asaas."
              }
              additionalDescriptionText={
                onlyDigits(values.postalCode) !== onlyDigits(data?.postalCode)
                  ? "Atenção: se o novo CEP for de outro município, a configuração de nota fiscal será zerada, as NFS-e agendadas serão canceladas e a emissão automática em assinaturas será interrompida."
                  : undefined
              }
              cancelText="Cancelar"
              confirmText="Salvar"
              maxWidth={560}
            />
          </form>
        );
      }}
    </Formik>
  );
}

function applyDocumentMaskSafe(doc: string) {
  const digits = onlyDigits(doc);
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (digits.length === 14)
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return doc;
}

export default CommercialInfoForm;
